import React, { useState, useEffect } from 'react';
import { learningHooksApi } from '../../services/api';

interface LearningHook {
  id: number;
  title: string;
  description: string;
  grade_levels: string;
  project_type: string;
  max_students: number;
  enrolled_count: number;
  is_active: boolean;
  created_at: string;
  teacher_name: string;
  why_content?: string;
  define_content?: string;
  do_content?: string;
  explain_content?: string;
  apply_content?: string;
  reflect_content?: string;
}

interface LearningHookCreatorProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  editingHook?: LearningHook | null;
}

interface FormData {
  title: string;
  description: string;
  grade_levels: string;
  project_type: string;
  max_students: string;
  why_content: string;
  define_content: string;
  do_content: string;
  explain_content: string;
  apply_content: string;
  reflect_content: string;
}

export const LearningHookCreator: React.FC<LearningHookCreatorProps> = ({ 
  onSuccess, 
  onCancel,
  editingHook
}) => {
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    grade_levels: '6th',
    project_type: 'individual',
    max_students: '30',
    why_content: '',
    define_content: '',
    do_content: '',
    explain_content: '',
    apply_content: '',
    reflect_content: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (editingHook) {
      setFormData({
        title: editingHook.title || '',
        description: editingHook.description || '',
        grade_levels: editingHook.grade_levels || '6th',
        project_type: editingHook.project_type || 'individual',
        max_students: (editingHook.max_students || 30).toString(),
        why_content: editingHook.why_content || '',
        define_content: editingHook.define_content || '',
        do_content: editingHook.do_content || '',
        explain_content: editingHook.explain_content || '',
        apply_content: editingHook.apply_content || '',
        reflect_content: editingHook.reflect_content || '',
      });
    }
  }, [editingHook]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const hookData = {
        title: formData.title,
        description: formData.description,
        grade_levels: formData.grade_levels,
        project_type: formData.project_type,
        max_students: parseInt(formData.max_students),
        why_content: formData.why_content,
        define_content: formData.define_content,
        do_content: formData.do_content,
        explain_content: formData.explain_content,
        apply_content: formData.apply_content,
        reflect_content: formData.reflect_content,
      };

      if (editingHook) {
        await learningHooksApi.update(editingHook.id, hookData);
      } else {
        await learningHooksApi.create(hookData);
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to save learning hook');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <div className="text-green-600 text-lg font-medium mb-2">
          ✓ {editingHook ? 'Learning Hook Updated Successfully!' : 'Learning Hook Created Successfully!'}
        </div>
        <p className="text-green-700">
          Your learning hook has been {editingHook ? 'updated' : 'created'} and is ready for students to enroll.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900">
        {editingHook ? 'Edit Learning Hook' : 'Create Learning Hook'}
      </h2>
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="border-b pb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Computer Hardware Basics"
              />
            </div>

            <div>
              <label htmlFor="grade_levels" className="block text-sm font-medium text-gray-700 mb-1">
                Grade Levels *
              </label>
              <select
                id="grade_levels"
                name="grade_levels"
                value={formData.grade_levels}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="6th">6th Grade</option>
                <option value="7th">7th Grade</option>
                <option value="8th">8th Grade</option>
                <option value="9th">9th Grade</option>
                <option value="10th">10th Grade</option>
                <option value="11th">11th Grade</option>
                <option value="12th">12th Grade</option>
              </select>
            </div>

            <div>
              <label htmlFor="project_type" className="block text-sm font-medium text-gray-700 mb-1">
                Project Type *
              </label>
              <select
                id="project_type"
                name="project_type"
                value={formData.project_type}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="individual">Individual Project</option>
                <option value="group">Group Project</option>
                <option value="class">Class Activity</option>
              </select>
            </div>

            <div>
              <label htmlFor="max_students" className="block text-sm font-medium text-gray-700 mb-1">
                Max Students (Optional)
              </label>
              <input
                type="number"
                id="max_students"
                name="max_students"
                value={formData.max_students}
                onChange={handleInputChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Leave empty for unlimited"
              />
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Brief description of the learning hook..."
            />
          </div>
        </div>

        {/* 6-Stage Content */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">6-Stage Learning Content</h3>
          <div className="space-y-4">
            {[
              { id: 'why_content', label: 'WHY - Foundation & Motivation', placeholder: 'Why is this important? What motivates students?' },
              { id: 'define_content', label: 'DEFINE - Vocabulary & Concepts', placeholder: 'Key terms, definitions, and concepts students need to know' },
              { id: 'do_content', label: 'DO - Practice Activities', placeholder: 'Hands-on activities and practice exercises' },
              { id: 'explain_content', label: 'EXPLAIN - Teaching Tasks', placeholder: 'Activities where students teach or explain concepts' },
              { id: 'apply_content', label: 'APPLY - Mastery Application', placeholder: 'Real-world application and mastery projects' },
              { id: 'reflect_content', label: 'REFLECT - Growth Reflection', placeholder: 'Reflection questions and growth mindset activities' }
            ].map((stage) => (
              <div key={stage.id}>
                <label htmlFor={stage.id} className="block text-sm font-medium text-gray-700 mb-1">
                  {stage.label} *
                </label>
                <textarea
                  id={stage.id}
                  name={stage.id}
                  value={formData[stage.id as keyof FormData]}
                  onChange={handleInputChange}
                  rows={3}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={stage.placeholder}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
          >
            {loading ? (editingHook ? 'Updating...' : 'Creating...') : (editingHook ? 'Update Learning Hook' : 'Create Learning Hook')}
          </button>
        </div>
      </form>
    </div>
  );
};
